import { ApiService, SyncServiceUrlServiceEnum } from "@incendium/api";
import { ElectricalServices } from "@mui/icons-material";
import { Button, CircularProgress } from "@mui/material";
import { syncService } from "Apis";
import { IConnectButtonProps } from "features/dataConnectors/types";
import { motion } from "motion/react";
import { useNotification } from "Hooks";
import useOAuthPopup from "Hooks/useOAuthPopup";
import { useSyncClients } from "Hooks/useSyncClients";
import produce from "immer";
import { useCallback } from "react";
import { OAUTH_REDIRECT_URL } from "features/auth/constants";
import { authenticateSyncClient } from "features/dataConnectors/utils/syncAuth";

function ConnectButton({
  service,
  syncClients,
  setSyncClients,
  oauthClientId,
  state,
  shop,
  disabled,
  linkAccountProjectId,
  onConnected,
  connectButtonProps,
  connectButtonLabel,
}: IConnectButtonProps) {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { refetch } = useSyncClients();

  const handleOAuthCode = useCallback(
    async (code: string) => {
      try {
        const res = await authenticateSyncClient({
          service,
          code,
          redirectUrl: OAUTH_REDIRECT_URL,
          shop,
          oauthClientId,
          linkAccountProjectId,
        });
        if (res) {
          setSyncClients(
            produce(syncClients, (draft) => {
              if (!draft[res.service as ApiService]) {
                draft[res.service as ApiService] = [];
              }
              draft[res.service as ApiService] = [
                ...draft[res.service as ApiService],
                res,
              ];
            }),
          );

          await refetch();

          showSuccessNotification(`${service} connected`);

          if (onConnected) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                onConnected(res);
              });
            });
          }
        }
      } catch {
        showErrorNotification(`Failed to connect ${service}`);
      }
    },
    [
      service,
      syncClients,
      setSyncClients,
      showSuccessNotification,
      showErrorNotification,
      oauthClientId,
      shop,
      linkAccountProjectId,
      onConnected,
      refetch,
    ],
  );

  const { startOAuth, isOAuthOpen } = useOAuthPopup({
    onCode: handleOAuthCode,
    cancelledMessage: "Store connection was denied",
  });

  const handleClick = useCallback(async () => {
    try {
      await startOAuth(async () => {
        const res = await syncService.syncServiceUrl({
          service: service as unknown as SyncServiceUrlServiceEnum,
          redirectUrl: OAUTH_REDIRECT_URL,
          state,
          shop,
        });

        if (!res.authUrl) {
          throw new Error("Missing auth URL");
        }

        return res.authUrl;
      });
    } catch {
      showErrorNotification(`Failed to start ${service} connection`);
    }
  }, [service, state, shop, startOAuth, showErrorNotification]);

  const loadingIcon = <CircularProgress size={20} color="inherit" />;
  const useCustomIcons =
    connectButtonProps?.startIcon !== undefined ||
    connectButtonProps?.endIcon !== undefined;
  const startIcon = useCustomIcons
    ? connectButtonProps?.startIcon
    : isOAuthOpen
      ? loadingIcon
      : <ElectricalServices />;
  const endIcon =
    useCustomIcons && isOAuthOpen && connectButtonProps?.endIcon
      ? loadingIcon
      : connectButtonProps?.endIcon;

  return (
    <Button
      size="small"
      variant="outlined"
      component={motion.div}
      initial={{
        opacity: 0,
        scale: 0.9,
      }}
      animate={{
        opacity: 1,
        scale: 1,
      }}
      {...connectButtonProps}
      startIcon={startIcon}
      endIcon={endIcon}
      onClick={handleClick}
      disabled={disabled || isOAuthOpen || connectButtonProps?.disabled}
    >
      {connectButtonLabel ?? (
        <>
          {oauthClientId && <>Re</>}connect
        </>
      )}
    </Button>
  );
}

export default ConnectButton;
